/**
 * Inline detail panel for an optimization run.
 *
 * Replaces the former full-overlay `ConvergenceRunDrawer`. This panel lives
 * inside the Runs tab's slide container (same `motion.div` as
 * `RunDetailsPanel`/`LiveRunPanel`) so the Runs list stays visible as a
 * back-target and no wide overlay is needed.
 *
 * Design goals (from product review):
 * - No raw UUID walls in the primary view — counts + human labels only.
 * - Collapsed 4-5 step stepper instead of the 7-phase bullet timeline in
 *   `apps/api/src/workflows/cache/convergence/report/render.ts:192`.
 * - LLM spend summarized (duration / model / cache-read) — per-agent-pass
 *   table hidden behind a disclosure that lazy-loads the full markdown report.
 *
 * @module ui/pages/FlowDesigner/runs/ConvergenceRunDetailPanel
 */
import React from 'react';
import { Link } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  DocsAnnotation,
  Flex,
  LoadingOrbit,
  Markdown,
  StatusIndicator,
  TagBadge,
  Text,
  TextArea,
} from '@chatsdet/ui';
import { ArrowLeft, ArrowRight, CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import type { ConvergenceRunHistoryRow } from '../hooks/useWorkflowRuns';
import {
  useConvergenceRun,
  useConvergenceRunReport,
  useResolveConvergenceReview,
  type ConvergenceBlocker,
} from '../../../../features/workflows/useConvergenceRun';
import { formatRelativeTimeAgo } from '../../../../features/workflows/useAgenticRunDetail';
import { formatDurationFromIso } from '../../../../lib/formatDuration';
import {
  CONVERGENCE_OUTCOME_VARIANTS,
  formatConvergenceLabel,
} from './convergence-run-formatters';

// ── Collapsed stepper ───────────────────────────────────────────────────────

type StepKey = 'setup' | 'converging' | 'verifying' | 'promoting' | 'done';

const STEPS: Array<{ key: StepKey; label: string }> = [
  { key: 'setup', label: 'Setup' },
  { key: 'converging', label: 'Caching' },
  { key: 'verifying', label: 'Verifying' },
  { key: 'promoting', label: 'Promoting' },
  { key: 'done', label: 'Done' },
];

type StepStatus = 'done' | 'active' | 'pending' | 'failed';

function phaseToStepIndex(phase: string | null): number {
  switch (phase) {
    case 'preflight':
    case 'fork':
      return 0;
    case 'converge':
      return 1;
    case 'verify':
      return 2;
    case 'promote':
    case 'origin_gate':
      return 3;
    case 'finalize':
      return 4;
    default:
      return -1;
  }
}

function stepStatusFor(
  stepIdx: number,
  currentStepIdx: number,
  isTerminal: boolean,
  terminalFailed: boolean
): StepStatus {
  if (isTerminal) {
    if (stepIdx < STEPS.length - 1) {
      // All pre-Done steps are done when the run terminated (with success
      // or not — they completed). If we want per-step failure marking, refine
      // with `reached` checks; for the collapsed view this is clearer.
      return currentStepIdx >= stepIdx || !terminalFailed ? 'done' : 'pending';
    }
    // Done step itself reflects the outcome
    return terminalFailed ? 'failed' : 'done';
  }
  if (stepIdx < currentStepIdx) return 'done';
  if (stepIdx === currentStepIdx) return 'active';
  return 'pending';
}

// ── Small helpers ─────────────────────────────────────────────────────────

function humanOutcome(outcome: string | null, promotedCount?: number, blockedCount?: number): string {
  if (!outcome) return 'In progress';
  if (outcome === 'converged') {
    return promotedCount !== undefined ? `Converged · ${promotedCount} nodes promoted` : 'Converged';
  }
  if (outcome === 'partially_converged' && promotedCount !== undefined) {
    const blocked = blockedCount ?? 0;
    return `Partially converged · ${promotedCount} promoted, ${blocked} blocked`;
  }
  if (outcome === 'awaiting_review') return 'Needs review';
  if (outcome === 'blocked') return 'Blocked';
  if (outcome === 'error') return 'Error';
  return formatConvergenceLabel(outcome);
}

function statusIndicatorVariant(
  outcome: string | null,
  status: string | null
): 'positive' | 'negative' | 'notice' | 'neutral' {
  if (outcome) {
    const v = CONVERGENCE_OUTCOME_VARIANTS[outcome];
    if (v === 'success') return 'positive';
    if (v === 'destructive') return 'negative';
    if (v === 'warning') return 'notice';
  }
  if (status === 'running' || status === 'queued') return 'notice';
  return 'neutral';
}

// ── Public props ──────────────────────────────────────────────────────────

interface ConvergenceRunDetailPanelProps {
  runId: string;
  summary?: ConvergenceRunHistoryRow | null;
  onBack: () => void;
}

export function ConvergenceRunDetailPanel({
  runId,
  summary,
  onBack,
}: ConvergenceRunDetailPanelProps) {
  const runQuery = useConvergenceRun(runId, { enabled: !!runId });
  const run = runQuery.data;

  // Prefer live detail; fall back to list row so header isn't blank while fetching
  const status = run?.status ?? summary?.status ?? null;
  const phase = run?.phase ?? summary?.phase ?? null;
  const outcome = run?.outcome ?? summary?.outcome ?? null;
  const cycleCount = run?.cycleCount ?? summary?.cycleCount ?? null;
  const modelId = run?.modelId ?? summary?.modelId ?? null;
  const triggeredBy = summary?.triggeredBy ?? null;
  const trigger = summary?.trigger ?? null;
  const startedAt = run?.startedAt ?? summary?.startedAt ?? null;
  const completedAt = run?.completedAt ?? summary?.finishedAt ?? null;
  const createdAt = run?.createdAt ?? summary?.createdAt ?? null;
  const cacheOnlyRunCount = run?.cacheOnlyRunCount ?? summary?.cacheOnlyRunCount ?? null;
  const requestedInstructions = run?.requestedInstructions ?? null;

  // Spec-mode runs converge in place, so `forkWorkflowId === workflowId` there
  // — only a real (non-spec-mode) fork is worth linking to, and only while it
  // still exists (converge/merge deletes it immediately, returning `null`).
  const forkWorkflowId =
    run?.forkWorkflowId && run.forkWorkflowId !== run.workflowId ? run.forkWorkflowId : null;

  const isRunning = status === 'queued' || status === 'running';
  const isTerminal = status === 'completed' || (!isRunning && !!outcome);
  const terminalFailed = !!outcome && outcome !== 'converged';

  const reportJson = run?.reportJson ?? null;
  const blockers: ConvergenceBlocker[] = run?.blockersJson ?? [];
  const terminalError = (reportJson?.error as string | undefined) ?? (reportJson?.abortReason as string | undefined) ?? null;

  // Counts for humanized copy (no UUIDs in primary view)
  const nodeMapCount = (reportJson?.nodeMap as { pairs?: unknown[] } | undefined)?.pairs?.length ?? null;
  const forkProofRunIds = (reportJson?.forkProofRunIds as string[] | undefined) ?? [];
  const originGateRunIds = (reportJson?.originGateRunIds as string[] | undefined) ?? [];
  const promotedNodes = (reportJson?.promotedNodes as Array<{ blocked?: boolean }> | undefined) ?? null;
  const promotedCount = promotedNodes ? promotedNodes.filter((n) => !n.blocked).length : null;
  const blockedCount = promotedNodes ? promotedNodes.filter((n) => n.blocked).length : null;
  const configEditsCount = (reportJson?.configEdits as unknown[] | undefined)?.length ?? 0;
  const promotionBlockedReason = (reportJson?.promotionBlockedReason as string | undefined) ?? null;

  const [showTechnical, setShowTechnical] = React.useState(false);
  const reportQuery = useConvergenceRunReport(runId, { enabled: showTechnical });

  const currentStepIdx = phaseToStepIndex(phase);

  const [isResolveOpen, setIsResolveOpen] = React.useState(false);
  const [resolveReason, setResolveReason] = React.useState('');
  const resolveMutation = useResolveConvergenceReview(runId, run?.workflowId ?? null);
  const canResolveReview = status === 'completed' && outcome === 'awaiting_review';

  const handleResolveReview = () => {
    const reason = resolveReason.trim();
    resolveMutation.mutate(reason.length > 0 ? reason : undefined, {
      onSuccess: () => {
        setIsResolveOpen(false);
        setResolveReason('');
      },
    });
  };

  // A deep link to a run that doesn't exist (or that the viewer can't see) has
  // no detail AND no list row to fall back on, so every header field below
  // resolves to null — which `humanOutcome` renders as a confident
  // "In progress" over an all-pending stepper. Say the run couldn't be loaded
  // instead of describing a run that isn't there.
  if (runQuery.isError && !run && !summary) {
    return (
      <DocsAnnotation slug="reference/runs" title="Optimization Run">
        <div className="flex flex-col h-full">
          <div className="px-4 py-3 border-b border-border">
            <Flex align="center" gap="sm">
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Back to Runs"
                aria-label="Back to runs"
                onPress={onBack}
                className="shrink-0"
              >
                <ArrowLeft size={16} />
              </Button>
              <TagBadge name="Optimization" color="gray" size="sm" hideDot className="shrink-0" />
            </Flex>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <Banner variant="error">
              <Text size="sm">
                {runQuery.error instanceof Error
                  ? runQuery.error.message
                  : 'Failed to load run details.'}
              </Text>
            </Banner>
          </div>
        </div>
      </DocsAnnotation>
    );
  }

  return (
    <DocsAnnotation slug="reference/runs" title="Optimization Run">
      <div className="flex flex-col h-full">
        {/* Header — mirrors RunDetailsPanel: back button + status pill */}
        <div className="px-4 py-3 border-b border-border space-y-2">
          <Flex justify="between" align="center" gap="sm">
            <Flex align="center" gap="sm">
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Back to Runs"
                aria-label="Back to runs"
                onPress={onBack}
                className="shrink-0"
              >
                <ArrowLeft size={16} />
              </Button>
              <StatusIndicator
                variant={statusIndicatorVariant(outcome, status)}
                size="sm"
                pulse={isRunning}
              >
                {isRunning && phase
                  ? formatConvergenceLabel(phase)
                  : outcome
                    ? formatConvergenceLabel(outcome)
                    : status
                      ? formatConvergenceLabel(status)
                      : 'Optimization'}
              </StatusIndicator>
              <TagBadge name="Optimization" color="gray" size="sm" hideDot className="shrink-0" />
            </Flex>
            {modelId && (
              <Text size="xs" className="text-muted-foreground font-mono truncate">
                {modelId}
              </Text>
            )}
          </Flex>

          <div className="space-y-1">
            {/* `Text`, not `Heading`: cofactor's unlayered `base.css` pins
                `.cofactor h1 { font-size: 24px }`, which silently outranks the
                `text-sm` a Heading would carry. RunDetailsPanel titles use Text
                for the same reason. */}
            <Text weight="semibold" className="text-[15px] leading-snug text-pretty">
              {humanOutcome(outcome, promotedCount ?? undefined, blockedCount ?? undefined)}
            </Text>
            <Flex align="center" gap="sm" wrap className="text-xs text-muted-foreground">
              <span>
                {triggeredBy?.displayName ?? triggeredBy?.email ?? (trigger && trigger !== 'manual' ? `auto: ${trigger}` : 'system')}
                {createdAt ? ` · ${formatRelativeTimeAgo(createdAt)}` : ''}
              </span>
              {(startedAt || completedAt) && (
                <>
                  <span>•</span>
                  <span>{formatDurationFromIso(startedAt, completedAt)}</span>
                </>
              )}
              {cacheOnlyRunCount !== null && (
                <>
                  <span>•</span>
                  <span>{cacheOnlyRunCount} cache-only runs</span>
                </>
              )}
              {cycleCount !== null && (
                <>
                  <span>•</span>
                  <span>
                    {cycleCount} attempt{cycleCount === 1 ? '' : 's'}
                  </span>
                </>
              )}
            </Flex>
            {forkWorkflowId && (
              <div>
                <Link
                  to="/flows/$workflowId"
                  params={{ workflowId: forkWorkflowId }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  Open fork
                  <ArrowRight size={12} />
                </Link>
                {outcome === 'awaiting_review' && (
                  <Text size="xs" className="text-muted-foreground ml-2 inline">
                    Review and merge from the fork
                  </Text>
                )}
              </div>
            )}
            {canResolveReview && (
              <div>
                <Button variant="outline" size="sm" onPress={() => setIsResolveOpen(true)}>
                  Resolve review
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogTrigger
          isOpen={isResolveOpen}
          onOpenChange={(open) => {
            if (resolveMutation.isPending) return;
            if (!open) resolveMutation.reset();
            setIsResolveOpen(open);
          }}
          size="sm"
        >
          <span />
          <Dialog title="Resolve review?">
            <DialogContent>
              <div className="space-y-4">
                <Text size="sm" className="text-muted-foreground text-pretty">
                  This drops the review without merging the fork. Automatic maintenance of this flow
                  will resume.
                </Text>
                <TextArea
                  label="Reason (optional)"
                  maxLength={500}
                  value={resolveReason}
                  onChange={setResolveReason}
                  rows={3}
                />
                {resolveMutation.isError && (
                  <Banner variant="error">
                    <Text size="sm">
                      {resolveMutation.error instanceof Error
                        ? resolveMutation.error.message
                        : 'Failed to resolve the review.'}
                    </Text>
                  </Banner>
                )}
              </div>
            </DialogContent>
            <DialogFooter>
              <Button
                variant="outline"
                onPress={() => setIsResolveOpen(false)}
                isDisabled={resolveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleResolveReview}
                isDisabled={resolveMutation.isPending}
              >
                {resolveMutation.isPending ? 'Resolving...' : 'Resolve review'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {runQuery.isLoading && !run ? (
            <Flex align="center" justify="center" className="py-10">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : runQuery.isError && !run ? (
            <Banner variant="error">
              <Text size="sm">
                {runQuery.error instanceof Error ? runQuery.error.message : 'Failed to load run details.'}
              </Text>
            </Banner>
          ) : null}

          {/* Stepper. Columns are `flex-1` so the labels get the panel's full
              width; the connectors are a fixed hairline so they don't collapse
              to a speck (the `w-16` fixed columns this replaced left only a
              ~3px line in the narrow runs panel). */}
          <nav aria-label="Optimization progress" className="flex items-start">
            {STEPS.map((step, idx) => {
              const st = stepStatusFor(idx, currentStepIdx, isTerminal, terminalFailed);
              return (
                <React.Fragment key={step.key}>
                  {idx > 0 && (
                    <div
                      className={`mt-3 h-px w-3 shrink-0 ${st === 'pending' || (isTerminal && terminalFailed && idx === STEPS.length - 1) ? 'bg-border' : 'bg-primary/40'}`}
                    />
                  )}
                  <div
                    className="flex min-w-0 flex-1 flex-col items-center gap-1"
                    aria-current={st === 'active' ? 'step' : undefined}
                    aria-label={`Step ${idx + 1}: ${step.label} — ${st}`}
                  >
                    <div
                      className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-medium ${
                        st === 'done'
                          ? 'border border-primary bg-primary text-primary-foreground'
                          : st === 'active'
                            ? 'border border-transparent'
                            : st === 'failed'
                              ? 'border border-destructive text-destructive'
                              : 'border border-border text-muted-foreground'
                      }`}
                    >
                      {st === 'done' ? (
                        <CheckCircle size={12} weight="fill" />
                      ) : st === 'active' ? (
                        <LoadingOrbit size="xs" />
                      ) : st === 'failed' ? (
                        <XCircle size={12} weight="fill" />
                      ) : (
                        idx + 1
                      )}
                    </div>
                    <span
                      className={`text-center text-[10px] font-medium leading-tight ${
                        st === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </nav>

          {/* Quick stats — human counts, no UUIDs */}
          {(nodeMapCount !== null || forkProofRunIds.length > 0 || promotedCount !== null) && (
            <div className="grid grid-cols-3 gap-2">
              {nodeMapCount !== null && (
                <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-center">
                  <Text size="xs" className="text-muted-foreground">
                    Mapped nodes
                  </Text>
                  <Text size="sm" weight="semibold" className="tabular-nums">
                    {nodeMapCount}
                  </Text>
                </div>
              )}
              <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-center">
                <Text size="xs" className="text-muted-foreground">
                  Proof runs
                </Text>
                <Text size="sm" weight="semibold" className="tabular-nums">
                  {forkProofRunIds.length + originGateRunIds.length || 0}
                </Text>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-center">
                <Text size="xs" className="text-muted-foreground">
                  Promoted
                </Text>
                <Text size="sm" weight="semibold" className="tabular-nums">
                  {promotedCount ?? 0}
                  {blockedCount ? ` · ${blockedCount} blocked` : ''}
                </Text>
              </div>
            </div>
          )}

          {requestedInstructions && (
            <section className="space-y-2">
              <Text weight="medium" size="sm">
                Requested focus
              </Text>
              <Text size="sm" className="whitespace-pre-wrap text-muted-foreground text-pretty">
                {requestedInstructions}
              </Text>
            </section>
          )}

          {terminalError && (
            <Banner variant={reportJson?.error ? 'error' : 'warning'}>
              <Flex align="start" gap="sm">
                <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <Text size="sm" weight="medium">
                    {reportJson?.error ? 'Run error' : 'Run aborted'}
                  </Text>
                  <Text size="sm">{terminalError}</Text>
                </div>
              </Flex>
            </Banner>
          )}

          {run?.stuck && (
            <div data-testid="convergence-stuck-reason">
              <Banner variant="warning">
                <Flex align="start" gap="sm">
                  <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <Text size="sm" weight="medium">
                      {run.stuck.label}
                    </Text>
                    {run.stuck.remediation && (
                      <Text size="sm" className="text-pretty">
                        {run.stuck.remediation}
                      </Text>
                    )}
                  </div>
                </Flex>
              </Banner>
            </div>
          )}

          {blockers.length > 0 && (
            <section className="space-y-2">
              <Text weight="medium" size="sm">Blockers</Text>
              <div className="space-y-2">
                {blockers.map((blocker, index) => (
                  <div
                    key={`${blocker.kind}-${index}`}
                    className="rounded-lg border border-border/60 p-3 space-y-1.5"
                  >
                    <Flex align="center" gap="sm" wrap>
                      <TagBadge name={blocker.kind} color="orange" size="sm" hideDot />
                    </Flex>
                    <Text size="sm" className="text-pretty">
                      {blocker.detail}
                    </Text>
                    {/* The stuck remediation shown above replaces the legacy proposal. */}
                    {blocker.proposal && !run?.stuck?.remediation && (
                      <Text size="sm" className="text-muted-foreground italic text-pretty">
                        Proposal: {blocker.proposal}
                      </Text>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {promotionBlockedReason && (
            <Banner variant="warning">
              <Text size="sm" weight="medium">
                Promotion blocked
              </Text>
              <Text size="sm" className="text-pretty">
                {promotionBlockedReason}
              </Text>
            </Banner>
          )}

          {(configEditsCount > 0 || promotedNodes) && (
            <section className="space-y-2">
              <Text weight="medium" size="sm">What changed</Text>
              <div className="rounded-lg border border-border/60 bg-card px-3 py-3 space-y-1.5">
                {configEditsCount > 0 && (
                  <Text size="sm">
                    <span className="font-medium">{configEditsCount}</span> config edit
                    {configEditsCount === 1 ? '' : 's'} applied
                  </Text>
                )}
                {promotedNodes && (
                  <Text size="sm" className="text-muted-foreground">
                    {promotedCount ?? 0} node{(promotedCount ?? 0) === 1 ? '' : 's'} promoted
                    {blockedCount ? ` · ${blockedCount} blocked` : ''} · {originGateRunIds.length} origin gate
                    run{originGateRunIds.length === 1 ? '' : 's'}
                  </Text>
                )}
                <Text size="xs" className="text-muted-foreground">
                  Full diffs & per-node persist details are in the technical report below.
                </Text>
              </div>
            </section>
          )}

          {/* Technical details — disclosure, lazy-loads full markdown */}
          <section className="space-y-2">
            <Flex align="center" justify="between">
              <Text weight="medium" size="sm">Technical details</Text>
              <Button
                variant="ghost"
                size="sm"
                onPress={() => setShowTechnical((v) => !v)}
                aria-expanded={showTechnical}
              >
                {showTechnical ? 'Hide' : 'Show'} full report
              </Button>
            </Flex>
            {!showTechnical ? (
              <Text size="xs" className="text-muted-foreground">
                Includes LLM spend, timings, config diffs and raw run IDs — hidden by default to keep the
                primary view readable.
              </Text>
            ) : reportQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-8">
                <LoadingOrbit size="md" />
              </Flex>
            ) : reportQuery.isError ? (
              <Banner variant="warning">
                <Text size="sm">
                  {reportQuery.error instanceof Error ? reportQuery.error.message : 'Failed to load the report.'}
                </Text>
              </Banner>
            ) : reportQuery.data?.markdown ? (
              <div className="rounded-lg border border-border bg-card p-4 overflow-x-auto">
                {/* The stored report is page-scale markdown (its `#` title is
                    24px under cofactor's unlayered `base.css:22`), which reads
                    oversized in this narrow panel. `cofactor:` + `!` is the
                    established way to beat that layer from a component — same
                    mechanism as `flow-list/components/cells.tsx` — and it is
                    brand-scoped so canary keeps Markdown's own prose scale. */}
                <Markdown className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground cofactor:[&_h1]:!text-[17px] cofactor:[&_h2]:!text-[15px] cofactor:[&_h3]:!text-[13.5px] cofactor:[&_h1]:!mt-0">
                  {reportQuery.data.markdown}
                </Markdown>
              </div>
            ) : (
              <Text size="sm" className="text-muted-foreground">
                {isRunning ? 'Report will appear once the run finishes a phase.' : 'No report generated for this run.'}
              </Text>
            )}
          </section>
        </div>
      </div>
    </DocsAnnotation>
  );
}
